import { Box, Fade, Typography, useMediaQuery, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useEffect, useRef, useState } from 'react';
import { Container, Heading, Text } from '../components/atomic';
import { FinalCTASection } from '../components/organisms';
import SEO from '../components/SEO';
import { StyledButton } from '../components/ui';
import { ContentPageLayout } from '../layouts';

/**
 * Data for a single horizontal bar in the skillsets chart.
 * @typedef {Object} SkillsetBar
 * @property {string} label - Skillset name (e.g. "AI Cost Management")
 * @property {number} value - Percentage value (0-100)
 */

/** @type {SkillsetBar[]} */
const SKILLSET_DATA = [
  { label: 'AI Cost Management', value: 58 },
  { label: 'FinOps Tooling', value: 43 },
  { label: 'Engineering / Automation Development', value: 40 },
  { label: 'Capacity Planning/Forecasting', value: 39 },
  { label: 'AI Engineering', value: 24 },
  { label: 'Infrastructure Architect', value: 17 },
];

const SKILLSET_SAMPLE_SIZE = 695;

/** EllaFin brand colors used across all chart components */
const COLORS = {
  navy: '#1e2140',
  accent: '#779a0a',
  mediumGreen: '#9ab43a',
  lightGreen: '#c4d68a',
  cream: '#f4f0eb',
  white: '#ffffff',
};

/**
 * Application window chrome wrapper that renders macOS-style title bar
 * with traffic light dots and EllaFin logo.
 *
 * @param {object} props - Component props
 * @param {string} props.title - Chart title displayed below the title bar
 * @param {string} props.href - External URL the card links to
 * @param {JSX.Element} props.children - SVG chart content
 * @returns {JSX.Element} Styled window card wrapping chart content
 */
function _ChartWindow({ title, href, children }) {
  return (
    <Box
      component="a"
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={`${title} - View on FinOps Foundation (opens in new tab)`}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: COLORS.white,
        borderRadius: '16px',
        boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
        overflow: 'hidden',
        textDecoration: 'none',
        color: 'inherit',
        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: '0 12px 40px rgba(0,0,0,0.22)',
        },
        '&:focus-visible': {
          outline: `3px solid ${COLORS.accent}`,
          outlineOffset: 2,
        },
      }}
    >
      {/* macOS-style title bar */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          backgroundColor: COLORS.navy,
          px: 1.5,
          py: 1,
          minHeight: 36,
        }}
      >
        {/* Traffic light dots */}
        <Box sx={{ display: 'flex', gap: 0.75 }}>
          <Box
            sx={{
              width: 10,
              height: 10,
              borderRadius: '50%',
              backgroundColor: '#ff5f57',
            }}
          />
          <Box
            sx={{
              width: 10,
              height: 10,
              borderRadius: '50%',
              backgroundColor: '#febc2e',
            }}
          />
          <Box
            sx={{
              width: 10,
              height: 10,
              borderRadius: '50%',
              backgroundColor: '#28c840',
            }}
          />
        </Box>
        {/* EllaFin logo */}
        <Box
          component="img"
          src={`${process.env.PUBLIC_URL}/images/full-logo-white-green.png`}
          alt=""
          aria-hidden="true"
          sx={{ height: 20, display: 'block' }}
        />
      </Box>

      {/* Chart title */}
      <Typography
        sx={{
          color: COLORS.navy,
          fontWeight: 700,
          fontSize: { xs: '0.75rem', sm: '0.8rem', md: '0.85rem' },
          px: 2,
          pt: 1.5,
          pb: 0.5,
          lineHeight: 1.3,
        }}
      >
        {title}
      </Typography>

      {/* Chart content */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: 1.5,
          pb: 1.5,
        }}
      >
        {children}
      </Box>
    </Box>
  );
}

/**
 * Bubble chart showing FinOps representation by geographic region.
 * Renders 4 proportionally-sized circles with percentage labels.
 *
 * @returns {JSX.Element} Inline SVG bubble chart
 */
function _BubbleChart() {
  return (
    <svg
      viewBox="0 0 320 200"
      width="100%"
      height="100%"
      aria-label="Bubble chart: FinOps Representation by Region"
    >
      <title>
        FinOps Representation by Region - Europe/ME/Africa 35%, North America
        34%, Asia Pacific 16%, South &amp; Central America 15%
      </title>
      {/* Europe/ME/Africa 35% */}
      <circle cx="85" cy="80" r="52" fill={COLORS.navy} />
      <text
        x="85"
        y="76"
        textAnchor="middle"
        fill={COLORS.white}
        fontSize="16"
        fontWeight="700"
      >
        35%
      </text>
      <text
        x="85"
        y="92"
        textAnchor="middle"
        fill={COLORS.white}
        fontSize="7"
        fontWeight="400"
      >
        Europe/ME/Africa
      </text>

      {/* North America 34% */}
      <circle cx="200" cy="78" r="51" fill={COLORS.navy} />
      <text
        x="200"
        y="74"
        textAnchor="middle"
        fill={COLORS.white}
        fontSize="16"
        fontWeight="700"
      >
        34%
      </text>
      <text
        x="200"
        y="90"
        textAnchor="middle"
        fill={COLORS.white}
        fontSize="7"
        fontWeight="400"
      >
        North America
      </text>

      {/* Asia Pacific 16% */}
      <circle cx="280" cy="105" r="32" fill={COLORS.accent} />
      <text
        x="280"
        y="103"
        textAnchor="middle"
        fill={COLORS.white}
        fontSize="13"
        fontWeight="700"
      >
        16%
      </text>
      <text
        x="280"
        y="115"
        textAnchor="middle"
        fill={COLORS.white}
        fontSize="6"
        fontWeight="400"
      >
        Asia Pacific
      </text>

      {/* South & Central America 15% */}
      <circle cx="145" cy="150" r="30" fill={COLORS.mediumGreen} />
      <text
        x="145"
        y="148"
        textAnchor="middle"
        fill={COLORS.white}
        fontSize="13"
        fontWeight="700"
      >
        15%
      </text>
      <text
        x="145"
        y="160"
        textAnchor="middle"
        fill={COLORS.white}
        fontSize="5.5"
        fontWeight="400"
      >
        S. &amp; C. America
      </text>
    </svg>
  );
}

/**
 * Calculates SVG arc path data for a donut chart segment.
 *
 * @param {number} cx - Center x coordinate
 * @param {number} cy - Center y coordinate
 * @param {number} outerR - Outer radius
 * @param {number} innerR - Inner radius
 * @param {number} startAngle - Start angle in degrees
 * @param {number} endAngle - End angle in degrees
 * @returns {string} SVG path d attribute string
 */
function describeArc(cx, cy, outerR, innerR, startAngle, endAngle) {
  const toRad = (deg) => (deg - 90) * (Math.PI / 180);
  const outerStart = {
    x: cx + outerR * Math.cos(toRad(startAngle)),
    y: cy + outerR * Math.sin(toRad(startAngle)),
  };
  const outerEnd = {
    x: cx + outerR * Math.cos(toRad(endAngle)),
    y: cy + outerR * Math.sin(toRad(endAngle)),
  };
  const innerStart = {
    x: cx + innerR * Math.cos(toRad(endAngle)),
    y: cy + innerR * Math.sin(toRad(endAngle)),
  };
  const innerEnd = {
    x: cx + innerR * Math.cos(toRad(startAngle)),
    y: cy + innerR * Math.sin(toRad(startAngle)),
  };
  const largeArc = endAngle - startAngle > 180 ? 1 : 0;

  return [
    `M ${outerStart.x} ${outerStart.y}`,
    `A ${outerR} ${outerR} 0 ${largeArc} 1 ${outerEnd.x} ${outerEnd.y}`,
    `L ${innerStart.x} ${innerStart.y}`,
    `A ${innerR} ${innerR} 0 ${largeArc} 0 ${innerEnd.x} ${innerEnd.y}`,
    'Z',
  ].join(' ');
}

/**
 * Donut chart showing FinOps team structure distribution.
 * Renders a 4-segment donut with label lines.
 *
 * @returns {JSX.Element} Inline SVG donut chart
 */
function _DonutChart() {
  const cx = 150;
  const cy = 100;
  const outerR = 65;
  const innerR = 35;
  const segments = [
    { label: 'Enabled Centrally', pct: 60, color: COLORS.navy },
    { label: 'Hub & Spoke', pct: 21, color: COLORS.accent },
    { label: 'No dedicated team', pct: 10, color: COLORS.mediumGreen },
    { label: 'Decentralized', pct: 9, color: COLORS.lightGreen },
  ];

  const toRad = (deg) => (deg - 90) * (Math.PI / 180);
  let currentAngle = 0;
  const arcs = segments.map((seg) => {
    const sweep = (seg.pct / 100) * 360;
    const startAngle = currentAngle;
    const endAngle = currentAngle + sweep;
    const midAngle = startAngle + sweep / 2;
    currentAngle = endAngle;
    return { ...seg, startAngle, endAngle, midAngle };
  });

  return (
    <svg
      viewBox="0 0 320 200"
      width="100%"
      height="100%"
      aria-label="Donut chart: FinOps Team Structure"
    >
      <title>
        FinOps Team Structure - Enabled Centrally 60%, Hub &amp; Spoke 21%, No
        dedicated team 10%, Decentralized 9%
      </title>
      {arcs.map((arc) => (
        <g key={arc.label}>
          <path
            d={describeArc(
              cx,
              cy,
              outerR,
              innerR,
              arc.startAngle,
              arc.endAngle
            )}
            fill={arc.color}
          />
          {/* Label line and text */}
          {(() => {
            const labelR = outerR + 12;
            const textR = outerR + 18;
            const mx = cx + labelR * Math.cos(toRad(arc.midAngle));
            const my = cy + labelR * Math.sin(toRad(arc.midAngle));
            const tx = cx + textR * Math.cos(toRad(arc.midAngle));
            const ty = cy + textR * Math.sin(toRad(arc.midAngle));
            const edgeX = cx + outerR * Math.cos(toRad(arc.midAngle));
            const edgeY = cy + outerR * Math.sin(toRad(arc.midAngle));
            const anchor = tx > cx ? 'start' : 'end';
            return (
              <>
                <line
                  x1={edgeX}
                  y1={edgeY}
                  x2={mx}
                  y2={my}
                  stroke={arc.color}
                  strokeWidth="1"
                />
                <text
                  x={tx}
                  y={ty - 3}
                  textAnchor={anchor}
                  fill={COLORS.navy}
                  fontSize="7"
                  fontWeight="600"
                >
                  {arc.pct}%
                </text>
                <text
                  x={tx}
                  y={ty + 6}
                  textAnchor={anchor}
                  fill={COLORS.navy}
                  fontSize="6"
                  opacity="0.7"
                >
                  {arc.label}
                </text>
              </>
            );
          })()}
        </g>
      ))}

      {/* Sample size */}
      <text
        x="318"
        y="195"
        textAnchor="end"
        fill={COLORS.navy}
        fontSize="7"
        opacity="0.6"
      >
        n=657
      </text>
    </svg>
  );
}

/**
 * Horizontal stacked bar chart showing FinOps team size by annual cloud spend.
 * Three spend tiers with practitioners (navy) and contractors (green) segments.
 *
 * @returns {JSX.Element} Inline SVG horizontal bar chart
 */
function _HorizontalBarChart() {
  const bars = [
    { label: '$100M+ Annual', practitioners: 8, contractors: 3 },
    { label: '$15M-$100M Annual', practitioners: 3, contractors: 1 },
    { label: 'Under $15M Annual', practitioners: 2, contractors: 1 },
  ];
  const maxTotal = 11;
  const barHeight = 28;
  const barGap = 20;
  const labelX = 2;
  const barStartX = 115;
  const barMaxWidth = 170;
  const startY = 30;

  return (
    <svg
      viewBox="0 0 320 180"
      width="100%"
      height="100%"
      aria-label="Horizontal bar chart: FinOps Team Size by Spend"
    >
      <title>
        FinOps Team Size by Spend - $100M+: 8 Practitioners + 3 Contractors,
        $15M-$100M: 3 + 1, Under $15M: 2 + 1
      </title>

      {/* Legend */}
      <rect x="115" y="8" width="10" height="10" rx="2" fill={COLORS.navy} />
      <text x="128" y="17" fill={COLORS.navy} fontSize="7" fontWeight="500">
        Practitioners
      </text>
      <rect x="200" y="8" width="10" height="10" rx="2" fill={COLORS.accent} />
      <text x="213" y="17" fill={COLORS.navy} fontSize="7" fontWeight="500">
        Contractors
      </text>

      {bars.map((bar, i) => {
        const y = startY + i * (barHeight + barGap);
        const pWidth = (bar.practitioners / maxTotal) * barMaxWidth;
        const cWidth = (bar.contractors / maxTotal) * barMaxWidth;
        return (
          <g key={bar.label}>
            {/* Spend tier label */}
            <text
              x={labelX}
              y={y + barHeight / 2 + 1}
              fill={COLORS.navy}
              fontSize="7.5"
              fontWeight="600"
              dominantBaseline="middle"
            >
              {bar.label}
            </text>
            {/* Practitioners bar */}
            <rect
              x={barStartX}
              y={y}
              width={pWidth}
              height={barHeight}
              rx="4"
              fill={COLORS.navy}
            />
            <text
              x={barStartX + pWidth / 2}
              y={y + barHeight / 2 + 1}
              textAnchor="middle"
              fill={COLORS.white}
              fontSize="8"
              fontWeight="700"
              dominantBaseline="middle"
            >
              {bar.practitioners}
            </text>
            {/* Contractors bar */}
            <rect
              x={barStartX + pWidth + 2}
              y={y}
              width={cWidth}
              height={barHeight}
              rx="4"
              fill={COLORS.accent}
            />
            <text
              x={barStartX + pWidth + 2 + cWidth / 2}
              y={y + barHeight / 2 + 1}
              textAnchor="middle"
              fill={COLORS.white}
              fontSize="8"
              fontWeight="700"
              dominantBaseline="middle"
            >
              {bar.contractors}
            </text>
          </g>
        );
      })}

      {/* Sample size */}
      <text
        x="318"
        y="175"
        textAnchor="end"
        fill={COLORS.navy}
        fontSize="7"
        opacity="0.6"
      >
        n=523
      </text>
    </svg>
  );
}

/**
 * Concentric circles chart showing AI spend management growth across years.
 * Three nested rings: 2026 (outer), 2025 (middle), 2024 (inner).
 *
 * @returns {JSX.Element} Inline SVG concentric circles chart
 */
function _ConcentricChart() {
  const cx = 120;
  const cy = 105;

  return (
    <svg
      viewBox="0 0 320 220"
      width="100%"
      height="100%"
      aria-label="Concentric circles chart: AI Spend Management Growth"
    >
      <title>
        AI Spend Management Growth - 2026: 98%, 2025: 63%, 2024: 31%
      </title>

      {/* Draw all circles first (back to front) */}
      <circle cx={cx} cy={cy} r="80" fill={COLORS.navy} />
      <circle cx={cx} cy={cy} r="55" fill={COLORS.accent} />
      <circle cx={cx} cy={cy} r="30" fill={COLORS.lightGreen} />

      {/* Percentage labels on top of circles */}
      <text
        x={cx}
        y={cy - 62}
        textAnchor="middle"
        fill={COLORS.white}
        fontSize="16"
        fontWeight="700"
      >
        98%
      </text>
      <text
        x={cx}
        y={cy - 35}
        textAnchor="middle"
        fill={COLORS.white}
        fontSize="14"
        fontWeight="700"
      >
        63%
      </text>
      <text
        x={cx}
        y={cy + 5}
        textAnchor="middle"
        fill={COLORS.navy}
        fontSize="12"
        fontWeight="700"
      >
        31%
      </text>

      {/* Year labels on the right, outside the rings */}
      <text
        x={cx + 95}
        y={cy - 60}
        fill={COLORS.navy}
        fontSize="10"
        fontWeight="700"
      >
        2026
      </text>
      <text
        x={cx + 95}
        y={cy - 10}
        fill={COLORS.navy}
        fontSize="10"
        fontWeight="700"
      >
        2025
      </text>
      <text
        x={cx + 95}
        y={cy + 40}
        fill={COLORS.navy}
        fontSize="10"
        fontWeight="700"
      >
        2024
      </text>

      {/* Connector lines from rings to year labels */}
      <line
        x1={cx + 78}
        y1={cy - 60}
        x2={cx + 92}
        y2={cy - 60}
        stroke={COLORS.navy}
        strokeWidth="0.5"
        opacity="0.4"
      />
      <line
        x1={cx + 53}
        y1={cy - 10}
        x2={cx + 92}
        y2={cy - 10}
        stroke={COLORS.navy}
        strokeWidth="0.5"
        opacity="0.4"
      />
      <line
        x1={cx + 28}
        y1={cy + 38}
        x2={cx + 92}
        y2={cy + 38}
        stroke={COLORS.navy}
        strokeWidth="0.5"
        opacity="0.4"
      />
    </svg>
  );
}

/**
 * Horizontal bar chart showing FinOps skillset demand data.
 *
 * Renders a styled card with title, subtitle, survey question, horizontal
 * bars with percentage labels, sample size, and source citation.
 *
 * @param {Object} props - Component props
 * @param {boolean} props.isVisible - Whether the section is in view (for animation)
 * @returns {JSX.Element} Rendered bar chart card
 */
function SkillsetBarChart({ isVisible }) {
  const theme = useTheme();
  const green = theme.palette.secondary.main;

  return (
    <Box
      sx={{
        backgroundColor: '#ffffff',
        border: '1px solid',
        borderColor: alpha('#000000', 0.12),
        borderRadius: '20px',
        p: { xs: 3, md: 4 },
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Chart Title */}
      <Box sx={{ mb: 1.5 }}>
        <Typography
          sx={{
            fontWeight: 700,
            fontStyle: 'italic',
            fontSize: { xs: '1.4rem', md: '1.6rem' },
            color: '#13152b',
            lineHeight: 1.2,
            fontFamily: 'Georgia, "Times New Roman", serif',
          }}
        >
          The State of FinOps{' '}
          <Box
            component="span"
            sx={{
              display: 'inline-block',
              backgroundColor: green,
              color: '#ffffff',
              fontStyle: 'normal',
              fontFamily: 'inherit',
              fontSize: '0.85em',
              fontWeight: 700,
              px: 1.5,
              py: 0.25,
              borderRadius: '999px',
              verticalAlign: 'middle',
              ml: 1,
            }}
          >
            2026
          </Box>
        </Typography>
      </Box>

      {/* Subtitle */}
      <Typography
        sx={{
          color: alpha('#13152b', 0.6),
          fontSize: { xs: '0.85rem', md: '0.95rem' },
          mb: 2.5,
          lineHeight: 1.4,
          fontWeight: 500,
        }}
      >
        Key data from the FinOps Foundation&rsquo;s annual report
      </Typography>

      {/* Survey Question */}
      <Typography
        sx={{
          fontStyle: 'italic',
          color: alpha('#13152b', 0.8),
          fontSize: { xs: '0.85rem', md: '0.92rem' },
          mb: 3,
          lineHeight: 1.5,
          fontWeight: 500,
        }}
      >
        &ldquo;Which of these skillsets are you looking to add to your FinOps
        practice in the next 12 months via people or tooling?&rdquo;
      </Typography>

      {/* Horizontal Bar Chart */}
      <Box
        sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1.5 }}
        role="img"
        aria-label="Bar chart showing skillsets FinOps teams plan to add"
      >
        {SKILLSET_DATA.map((item, index) => (
          <Box key={item.label}>
            {/* Label row */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'baseline',
                mb: 0.5,
              }}
            >
              <Typography
                sx={{
                  fontSize: { xs: '0.78rem', md: '0.85rem' },
                  fontWeight: 600,
                  color: '#13152b',
                  lineHeight: 1.3,
                }}
              >
                {item.label}
              </Typography>
              <Typography
                sx={{
                  fontSize: { xs: '0.85rem', md: '0.92rem' },
                  fontWeight: 700,
                  color: '#13152b',
                  ml: 1,
                  flexShrink: 0,
                }}
              >
                {item.value}%
              </Typography>
            </Box>
            {/* Bar */}
            <Box
              sx={{
                width: '100%',
                height: 10,
                backgroundColor: alpha('#13152b', 0.08),
                borderRadius: '5px',
                overflow: 'hidden',
              }}
            >
              <Box
                sx={{
                  width: isVisible ? `${item.value}%` : '0%',
                  height: '100%',
                  backgroundColor:
                    index === 0 ? green : alpha(green, 0.55 + index * 0.02),
                  borderRadius: '5px',
                  transition: `width 1s ease ${0.2 + index * 0.1}s`,
                }}
              />
            </Box>
          </Box>
        ))}
      </Box>

      {/* Sample size */}
      <Typography
        sx={{
          mt: 2.5,
          fontSize: '0.8rem',
          fontWeight: 600,
          color: '#4d4d6a',
        }}
      >
        N={SKILLSET_SAMPLE_SIZE}
      </Typography>

      {/* Source link */}
      <Typography
        sx={{
          mt: 1,
          fontSize: '0.8rem',
          fontWeight: 500,
          color: '#4d4d6a',
        }}
      >
        Source:{' '}
        <Box
          component="a"
          href="https://data.finops.org/library/"
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            color: '#1e2140',
            textDecoration: 'underline',
            '&:hover': {
              opacity: 0.7,
            },
          }}
        >
          State of FinOps 2026 Report — FinOps Foundation
        </Box>
      </Typography>
    </Box>
  );
}

/**
 * About/Company page component for EllaFin
 *
 * Simplified 3-section design matching Homepage/Product visual language:
 * - Section 1: Hero with solid background and dual CTAs
 * - Section 2: Mission/Vision in clean two-column layout
 * - Section 3: FinalCTASection
 *
 * Clean, minimal, professional - no heavy cards or clutter.
 * Uses design tokens for consistent theming.
 *
 * @returns {JSX.Element} About page component
 */
function About() {
  const [isLoaded, setIsLoaded] = useState(false);
  const [_missionVisible, setMissionVisible] = useState(false);
  const [_chartsVisible, setChartsVisible] = useState(false);
  const [collectiveVisible, setCollectiveVisible] = useState(false);
  const [skillsetVisible, setSkillsetVisible] = useState(false);
  const missionRef = useRef(null);
  const chartsRef = useRef(null);
  const collectiveRef = useRef(null);
  const skillsetRef = useRef(null);
  const theme = useTheme();
  const _isMobile = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    setIsLoaded(true);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setMissionVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (missionRef.current) {
      observer.observe(missionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setChartsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (chartsRef.current) {
      observer.observe(chartsRef.current);
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setCollectiveVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (collectiveRef.current) {
      observer.observe(collectiveRef.current);
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setSkillsetVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (skillsetRef.current) {
      observer.observe(skillsetRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <ContentPageLayout backgroundColor={theme.palette.background.section}>
      <SEO
        title="About EllaFin - Why Finance-First FinOps"
        description="Learn why finance teams need cloud cost management that fits their workflows. EllaFin gives everyone from engineers to executives a shared view of cloud costs."
        path="/about"
        keywords="finance-first FinOps, cloud cost management, FinOps mission, AWS cost clarity, RSM Collective"
        structuredData={{
          '@context': 'https://schema.org',
          '@type': 'AboutPage',
          name: 'About EllaFin',
          description:
            'Why Finance-First FinOps matters. EllaFin gives finance teams cloud cost clarity in the language of finance.',
          url: 'https://www.ellafin.ai/about',
          mainEntity: {
            '@type': 'Organization',
            name: 'EllaFin',
            url: 'https://www.ellafin.ai',
            address: {
              '@type': 'PostalAddress',
              streetAddress: '401 W A Street Ste. 200',
              addressLocality: 'San Diego',
              addressRegion: 'CA',
              postalCode: '92101',
              addressCountry: 'US',
            },
          },
        }}
      />

      {/* Section 1: Hero -- Bold dark navy */}
      <Box
        component="section"
        aria-label="About hero"
        sx={{
          position: 'relative',
          minHeight: { xs: 'auto', md: '85vh' },
          display: 'flex',
          alignItems: 'center',
          py: { xs: 10, md: 0 },
          backgroundColor: '#1e2140',
          overflow: 'hidden',
        }}
      >
        <Container maxWidth="lg">
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
              gap: { xs: 6, md: 8 },
              alignItems: 'center',
            }}
          >
            {/* Left: headline + subtitle + CTA */}
            <Fade in={isLoaded} timeout={800}>
              <Box>
                <Heading
                  variant="h1"
                  sx={{
                    fontSize: {
                      xs: '2.5rem',
                      sm: '3rem',
                      md: '3.5rem',
                      lg: '4rem',
                    },
                    fontWeight: 700,
                    lineHeight: 1.05,
                    color: '#ffffff',
                    letterSpacing: '-0.02em',
                    mb: 3,
                  }}
                >
                  Why Finance-First{' '}
                  <Box
                    component="span"
                    sx={{ color: theme.palette.secondary.main }}
                  >
                    Fin
                  </Box>
                  Ops?
                </Heading>

                <Text
                  sx={{
                    fontSize: { xs: '1rem', md: '1.1rem' },
                    color: alpha('#ffffff', 0.8),
                    lineHeight: 1.8,
                    mb: 3,
                  }}
                >
                  The team at RSM Collective has spent years working with AWS
                  clients: datacenter migrations, architecting SaaS solutions,
                  and building serverless applications. During that time, one
                  pattern kept emerging, finance teams needed cloud cost
                  management that fit their workflows, not the other way around.
                </Text>

                <Text
                  sx={{
                    fontSize: { xs: '1.1rem', md: '1.25rem' },
                    color: alpha('#ffffff', 0.7),
                    lineHeight: 1.7,
                    mb: 4,
                    fontStyle: 'italic',
                  }}
                >
                  EllaFin&#8482; gives everyone, from engineers to executives, a
                  shared view of cloud costs enabling you to build FinOps
                  maturity, at your own pace, with collaboration across every
                  team.
                </Text>

                <StyledButton
                  variant="outlined"
                  size="large"
                  onClick={() => {
                    window.location.href = '/early-access';
                  }}
                  sx={{
                    px: 4,
                    py: 1.75,
                    fontSize: '1.1rem',
                    color: '#ffffff',
                  }}
                >
                  Get Early Access
                </StyledButton>
              </Box>
            </Fade>

            {/* Right: feature cards */}
            <Fade in={isLoaded} timeout={1200}>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: 2.5,
                }}
              >
                {[
                  {
                    title: 'Cost Center Mapping',
                    desc: 'Map AWS spend to your existing cost centers and budget categories regardless of tagging hygiene.',
                  },
                  {
                    title: 'Engineering Context',
                    desc: 'Give engineering teams clear, real time cost context so they can make informed architectural decisions.',
                  },
                  {
                    title: 'Financial Workflows',
                    desc: 'Align cloud spend reporting to monthly close, variance analysis, and forecasting workflows.',
                  },
                  {
                    title: 'Executive Reporting',
                    desc: 'Deliver executive-ready reporting that ties AWS usage to business impact, not just engineering metrics.',
                  },
                ].map((card) => (
                  <Box
                    key={card.title}
                    sx={{
                      backgroundColor: alpha('#ffffff', 0.04),
                      p: 3,
                      borderLeft: `4px solid ${theme.palette.secondary.main}`,
                    }}
                  >
                    <Typography
                      sx={{
                        color: '#ffffff',
                        fontWeight: 700,
                        fontSize: '0.95rem',
                        mb: 1,
                      }}
                    >
                      {card.title}
                    </Typography>
                    <Typography
                      sx={{
                        color: alpha('#ffffff', 0.7),
                        fontSize: '0.85rem',
                        lineHeight: 1.6,
                      }}
                    >
                      {card.desc}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Fade>
          </Box>
        </Container>
      </Box>

      {/* Section 2: RSM Collective */}
      <Box
        ref={collectiveRef}
        component="section"
        aria-label="About The Collective"
        sx={{
          py: { xs: 10, md: 16 },
          backgroundColor: '#13152b',
        }}
      >
        <Container maxWidth="lg">
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: { xs: 'stretch', md: 'center' },
              gap: { xs: 6, md: 0 },
              opacity: collectiveVisible ? 1 : 0,
              transform: collectiveVisible
                ? 'translateY(0)'
                : 'translateY(30px)',
              transition: 'all 0.8s ease',
            }}
          >
            {/* Left: heading + body */}
            <Box sx={{ flex: 1 }}>
              <Heading
                variant="h2"
                sx={{
                  fontSize: {
                    xs: '2rem',
                    sm: '2.5rem',
                    md: '3rem',
                  },
                  fontWeight: 700,
                  lineHeight: 1.1,
                  color: '#ffffff',
                  letterSpacing: '-0.02em',
                  mb: 4,
                }}
              >
                RSM Collective
              </Heading>

              <Text
                sx={{
                  fontSize: { xs: '1rem', md: '1.1rem' },
                  color: alpha('#ffffff', 0.8),
                  lineHeight: 1.8,
                  mb: 2.5,
                }}
              >
                RSM Collective is a community of consultants, technology
                vendors, and system integrators committed to transforming the
                employee, the patient, the civic and the customer experience
                with cloud computing.
              </Text>

              <Text
                sx={{
                  fontSize: { xs: '1rem', md: '1.1rem' },
                  color: alpha('#ffffff', 0.8),
                  lineHeight: 1.8,
                  mb: 2.5,
                }}
              >
                Rooted in diverse backgrounds, perspectives, and lived
                experiences, we bring a unique ability to create what others
                might not even imagine.
              </Text>

              <Text
                sx={{
                  fontSize: { xs: '1rem', md: '1.1rem' },
                  color: alpha('#ffffff', 0.8),
                  lineHeight: 1.8,
                }}
              >
                We believe modernization isn&rsquo;t a solo pursuit, it is a
                collaborative journey with your employees, clients and partners.
                The most transformative outcomes happen when they&rsquo;re
                powered by a collective of diverse voices.
              </Text>
            </Box>

            {/* Green vertical accent divider */}
            <Box
              sx={{
                display: { xs: 'none', md: 'block' },
                width: '4px',
                alignSelf: 'stretch',
                backgroundColor: theme.palette.secondary.main,
                mx: 6,
                borderRadius: '2px',
              }}
            />

            {/* Right: tagline */}
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Typography
                sx={{
                  fontFamily: '"Playfair Display", "Georgia", serif',
                  fontSize: {
                    xs: '2rem',
                    sm: '2.5rem',
                    md: '3rem',
                    lg: '3.75rem',
                  },
                  fontWeight: 700,
                  lineHeight: 1.1,
                  color: '#f4f0eb',
                  textTransform: 'uppercase',
                  letterSpacing: '0.02em',
                  textAlign: { xs: 'center', md: 'left' },
                }}
              >
                When People Thrive, Creativity Follows&#8482;
              </Typography>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* COMMENTED OUT: Section 3: Mission & Vision - Clean Two Column Typography */}
      {/* <Box
        ref={missionRef}
        component="section"
        aria-label="Mission and vision"
        sx={{
          py: { xs: 10, md: 14 },
          backgroundColor: designTokens.background,
        }}
      >
        <Container maxWidth="lg">
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
              gap: { xs: 6, md: 10 },
              opacity: missionVisible ? 1 : 0,
              transform: missionVisible ? 'translateY(0)' : 'translateY(30px)',
              transition: 'all 0.8s ease',
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Box
                component="img"
                src={`${process.env.PUBLIC_URL}/images/cloudcosts.jpeg`}
                alt="Cloud costs in the language of finance"
                sx={{
                  width: '100%',
                  height: 200,
                  objectFit: 'cover',
                  borderRadius: '16px',
                  mb: 3,
                }}
              />
              <Typography
                variant="overline"
                sx={{
                  color: designTokens.primary,
                  fontWeight: 600,
                  letterSpacing: '0.1em',
                  fontSize: '0.8rem',
                  mb: 2,
                  display: 'block',
                }}
              >
                Our Mission
              </Typography>
              <Heading
                variant="h2"
                sx={{
                  fontSize: { xs: '1.75rem', md: '2.25rem' },
                  fontWeight: 700,
                  color: designTokens.primary,
                  mb: 2,
                  lineHeight: 1.2,
                }}
              >
                Cloud Costs in the Language of Finance
              </Heading>
              <Text
                sx={{
                  fontSize: { xs: '1rem', md: '1.1rem' },
                  lineHeight: 1.8,
                  color: '#4a5568',
                }}
              >
                To give finance teams the cloud cost clarity they deserve -
                translating complex AWS spending into the financial language
                that drives business decisions. No more spreadsheet gymnastics
                or waiting on engineering for cost data.
              </Text>
              <Box sx={{ mt: 'auto', pt: 3, textAlign: 'center' }}>
                <StyledButton
                  variant="contained"
                  size="large"
                  onClick={() => {
                    window.location.href = '/early-access';
                  }}
                  sx={{ px: 4, py: 1.75, fontSize: '1.1rem' }}
                >
                  Get Early Access
                </StyledButton>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Box
                component="img"
                src={`${process.env.PUBLIC_URL}/images/AdobeStock_1890471615.jpeg`}
                alt="FinOps data analytics dashboard"
                sx={{
                  width: '100%',
                  height: 200,
                  objectFit: 'cover',
                  borderRadius: '16px',
                  mb: 3,
                }}
              />
              <Typography
                variant="overline"
                sx={{
                  color: designTokens.primary,
                  fontWeight: 600,
                  letterSpacing: '0.1em',
                  fontSize: '0.8rem',
                  mb: 2,
                  display: 'block',
                }}
              >
                Our Vision
              </Typography>
              <Heading
                variant="h2"
                sx={{
                  fontSize: { xs: '1.75rem', md: '2.25rem' },
                  fontWeight: 700,
                  color: designTokens.primary,
                  mb: 2,
                  lineHeight: 1.2,
                }}
              >
                FinOps That Speaks Finance
              </Heading>
              <Text
                sx={{
                  fontSize: { xs: '1rem', md: '1.1rem' },
                  lineHeight: 1.8,
                  color: '#4a5568',
                }}
              >
                A world where every finance professional can confidently manage
                cloud spending. Where FinOps speaks the language of finance, not
                engineering. Where cloud costs align seamlessly with your chart
                of accounts.
              </Text>
              <Box sx={{ mt: 'auto', pt: 3, textAlign: 'center' }}>
                <StyledButton
                  variant="outlined"
                  size="large"
                  onClick={() => {
                    window.location.href = '/product';
                  }}
                  sx={{ px: 4, py: 1.75, fontSize: '1.1rem' }}
                >
                  Learn More
                </StyledButton>
              </Box>
            </Box>
          </Box>
        </Container>
      </Box> */}

      {/* Section: FinOps Skillset Demand - Bar Chart + Narrative */}
      <Box
        ref={skillsetRef}
        component="section"
        aria-label="FinOps skillset demand insights"
        sx={{
          py: { xs: 14, md: 22 },
          backgroundColor: '#13152b',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Container maxWidth="lg">
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              gap: { xs: 6, md: 6 },
              alignItems: 'stretch',
              opacity: skillsetVisible ? 1 : 0,
              transform: skillsetVisible ? 'translateY(0)' : 'translateY(30px)',
              transition: 'all 0.8s ease',
            }}
          >
            {/* LEFT SIDE: Skillset Bar Chart Card */}
            <Box sx={{ flex: { xs: '1 1 auto', md: '0 0 46%' } }}>
              <SkillsetBarChart isVisible={skillsetVisible} />
            </Box>

            {/* RIGHT SIDE: Dark content card */}
            <Box sx={{ flex: { xs: '1 1 auto', md: '1 1 0%' } }}>
              <Box
                sx={{
                  backgroundColor: alpha('#ffffff', 0.08),
                  borderRadius: '20px',
                  p: { xs: 3, md: 5 },
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                }}
              >
                {/* Heading */}
                <Typography
                  variant="h4"
                  component="h3"
                  sx={{
                    color: '#ffffff',
                    fontWeight: 700,
                    fontSize: { xs: '1.6rem', md: '2rem' },
                    lineHeight: 1.3,
                    mb: 3,
                  }}
                >
                  The Skills{' '}
                  <Box
                    component="span"
                    sx={{
                      color: theme.palette.secondary.main,
                      fontWeight: 900,
                    }}
                  >
                    Fin
                  </Box>
                  Ops Teams Need Most.{' '}
                  <Box
                    component="span"
                    sx={{ display: { xs: 'inline', md: 'block' } }}
                  >
                    We Have Them.
                  </Box>
                </Typography>

                <Typography
                  sx={{
                    color: alpha('#ffffff', 0.85),
                    fontSize: { xs: '0.95rem', md: '1.05rem' },
                    lineHeight: 1.75,
                    mb: 3,
                  }}
                >
                  The 2026 State of FinOps report asked 695 practitioners what
                  skillsets they need to add in the next 12 months. The answers
                  tell a clear story.
                </Typography>

                <Typography
                  sx={{
                    color: alpha('#ffffff', 0.85),
                    fontSize: { xs: '0.95rem', md: '1.05rem' },
                    lineHeight: 1.75,
                    mb: 3,
                  }}
                >
                  AI cost management stands out as the single most desired
                  skillset at 58%, regardless of organization size. This
                  reflects both the rapid growth of AI-related spend and the
                  complexity of understanding and allocating those costs.
                  Tooling expertise (43%) and automation development (40%)
                  follow closely, reinforcing that FinOps teams aren&rsquo;t
                  just looking for dashboards. They need people who can build,
                  integrate, and automate.
                </Typography>

                <Typography
                  sx={{
                    color: alpha('#ffffff', 0.85),
                    fontSize: { xs: '0.95rem', md: '1.05rem' },
                    lineHeight: 1.75,
                  }}
                >
                  As an AWS technology and consulting partner, RSM Collective
                  delivers across all six. EllaFin&trade; and our Modernization
                  consulting services are grounded in the FinOps Framework,
                  ensuring forecasting, capacity planning, and infrastructure
                  architecture decisions are made with financial context, not
                  after the fact.
                </Typography>
              </Box>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* COMMENTED OUT: Section: State of FinOps Charts */}
      {/* <Box
        ref={chartsRef}
        component="section"
        aria-label="State of FinOps 2026 data"
        sx={{
          py: { xs: 10, md: 14 },
          backgroundColor: '#13152b',
        }}
      >
        <Container maxWidth="lg">
          <Box
            sx={{
              textAlign: 'center',
              mb: { xs: 4, md: 6 },
              opacity: chartsVisible ? 1 : 0,
              transform: chartsVisible ? 'translateY(0)' : 'translateY(30px)',
              transition: 'all 0.8s ease',
            }}
          >
            <Heading
              variant="h2"
              sx={{
                fontSize: {
                  xs: '1.75rem',
                  sm: '2.25rem',
                  md: '2.75rem',
                },
                fontWeight: 700,
                lineHeight: 1.2,
                color: '#ffffff',
                letterSpacing: '-0.02em',
                mb: 1.5,
              }}
            >
              The State of FinOps 2026
            </Heading>
            <Text
              sx={{
                fontSize: { xs: '1rem', md: '1.15rem' },
                color: alpha('#ffffff', 0.7),
                lineHeight: 1.6,
                maxWidth: '600px',
                mx: 'auto',
              }}
            >
              Key data from the FinOps Foundation's annual report
            </Text>
          </Box>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
              gap: 3,
              width: '100%',
              maxWidth: '900px',
              mx: 'auto',
              px: { xs: 1, md: 0 },
              opacity: chartsVisible ? 1 : 0,
              transform: chartsVisible ? 'translateY(0)' : 'translateY(30px)',
              transition: 'all 0.8s ease 0.2s',
            }}
          >
            <ChartWindow
              title="FinOps Representation by Region"
              href="https://data.finops.org/library/#26850"
            >
              <BubbleChart />
            </ChartWindow>

            <ChartWindow
              title="FinOps Team Structure"
              href="https://data.finops.org/library/#26858"
            >
              <DonutChart />
            </ChartWindow>

            <ChartWindow
              title="FinOps Team Size by Spend"
              href="https://data.finops.org/library/#26861"
            >
              <HorizontalBarChart />
            </ChartWindow>

            <ChartWindow
              title="AI Spend Management Growth"
              href="https://data.finops.org/library/#26891"
            >
              <ConcentricChart />
            </ChartWindow>
          </Box>

          <Typography
            component="a"
            href="https://data.finops.org/"
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              display: 'block',
              textAlign: 'center',
              mt: { xs: 3, md: 4 },
              fontSize: { xs: '0.8rem', md: '0.85rem' },
              color: alpha('#ffffff', 0.5),
              textDecoration: 'none',
              fontStyle: 'italic',
              opacity: chartsVisible ? 1 : 0,
              transition: 'opacity 0.8s ease 0.4s',
              '&:hover': {
                color: alpha('#ffffff', 0.7),
              },
            }}
          >
            Source: State of FinOps 2026 Report — FinOps Foundation
          </Typography>
        </Container>
      </Box> */}

      {/* Final CTA */}
      <FinalCTASection
        sectionId="about-cta"
        headline="Join Us on Our Mission"
        subheadline="Be part of the Finance-First FinOps revolution. Join our early access program and help shape the future of cloud cost management."
        primaryCtaText="Get Early Access"
        primaryCtaHref="/early-access"
        secondaryCtaText="Learn More"
        secondaryCtaHref="/product"
      />
    </ContentPageLayout>
  );
}

export default About;
